import React, { useState, useRef, useMemo, useEffect } from 'react';
import '../../CSS/ReportDefinition.css';
import api from '../../api';
import { useRights } from "../../Components/RightsContext.jsx";

const ReportDefinition_PATH = "/Reports/ReportDefinition";

/* ════════ AUTOCOMPLETE (searchable dropdown) ════════ */
// Only the first AC_MAX_SHOWN matches are drawn so 15,000+ parties don't freeze the page.
const AC_MAX_SHOWN = 100;
function AutoComplete({ options = [], value, onChange, placeholder = 'Search…', disabled = false }) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [focused, setFocused] = useState(-1);
  const [cursorMoved, setCursorMoved] = useState(false);
  const wrapRef = useRef(null);
  const optionRefs = useRef([]);

  const selected = options.find((o) => String(o.value) === String(value));

  useEffect(() => {
    if (selected) setQuery(selected.label);
    else if (!value) setQuery('');
  }, [value, selected]);

  const q = (query || '').toLowerCase();
  const filtered = options.filter((o) => o.value !== undefined && o.label != null && String(o.label).toLowerCase().includes(q));
  const shown = filtered.length > AC_MAX_SHOWN ? filtered.slice(0, AC_MAX_SHOWN) : filtered;

  useEffect(() => {
    if (focused >= 0 && optionRefs.current[focused]) {
      try { optionRefs.current[focused].scrollIntoView({ block: 'nearest' }); } catch (err) { /* noop */ }
    }
  }, [focused]);

  function hl(text) {
    if (!query) return text;
    const str = String(text);
    const i = str.toLowerCase().indexOf(q);
    if (i < 0) return str;
    return (<>{str.slice(0, i)}<mark>{str.slice(i, i + query.length)}</mark>{str.slice(i + query.length)}</>);
  }

  function resetQuery() {
    if (selected) setQuery(selected.label);
    else if (!value) setQuery('');
  }
  function choose(opt) {
    onChange(String(opt.value));
    setQuery(opt.label);
    setIsOpen(false);
    setFocused(-1);
    setCursorMoved(false);
  }
  function clear() {
    onChange('');
    setQuery('');
    setIsOpen(true);
    setFocused(-1);
  }

  useEffect(() => {
    function handle(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setIsOpen(false);
        resetQuery();
      }
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, value]);

  function handleKeyDown(e) {
    if (disabled) return;
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { setIsOpen(true); setCursorMoved(true); }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault(); setCursorMoved(true);
      setFocused((f) => Math.min(f + 1, shown.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault(); setCursorMoved(true);
      setFocused((f) => Math.max(f - 1, 0));
    } else if (e.key === 'Escape') {
      setIsOpen(false); resetQuery();
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (cursorMoved && shown.length > 0) {
        const target = shown[focused] || shown[0];
        if (target) choose(target);
      } else if (filtered.length === 1) {
        choose(filtered[0]);
      } else {
        setIsOpen(false);
      }
    }
  }

  return (
    <div className="red-ac" ref={wrapRef}>
      <div className={'red-ac-box' + (isOpen ? ' red-ac-open' : '') + (disabled ? ' red-ac-disabled' : '')}>
        <input
          className="red-ac-in"
          type="text"
          autoComplete="off"
          disabled={disabled}
          placeholder={placeholder}
          value={query}
          onFocus={() => { if (!disabled) { setIsOpen(true); setCursorMoved(false); } }}
          onClick={() => { if (!disabled && !isOpen) setIsOpen(true); }}
          onChange={(e) => { if (disabled) return; setQuery(e.target.value); setIsOpen(true); setFocused(-1); }}
          onKeyDown={handleKeyDown}
        />
        {value && !disabled && (
          <span className="red-ac-x" onMouseDown={(e) => { e.preventDefault(); clear(); }}>×</span>
        )}
        <span className="red-ac-chev" onMouseDown={(e) => { if (disabled) return; e.preventDefault(); setIsOpen((o) => !o); }}>▾</span>
      </div>
      {isOpen && !disabled && (
        <div className="red-ac-drop">
          {filtered.length === 0 ? (
            <div className="red-ac-empty">No matches found</div>
          ) : (
            <>
              {shown.map((opt, i) => (
                <div
                  key={opt.value}
                  ref={(el) => { optionRefs.current[i] = el; }}
                  className={'red-ac-opt' + (String(opt.value) === String(value) ? ' red-ac-sel' : '') + (i === focused ? ' red-ac-foc' : '')}
                  onMouseDown={(e) => { e.preventDefault(); choose(opt); }}
                  onMouseEnter={() => setFocused(i)}
                >
                  {hl(opt.label)}
                </div>
              ))}
              {filtered.length > AC_MAX_SHOWN && (
                <div className="red-ac-empty">Showing first {AC_MAX_SHOWN} of {filtered.length} — type to narrow</div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ════════ MASTER FIELD LIST ════════
   No longer hardcoded — fetched from GET /get-report-fields (see `fields`
   state below). SAMPLE stays as a fallback preview until a real saved
   report has backend data. */
const SAMPLE = {
  cnr: 'STL-2604-00847', bkbranch: 'Mumbai', bkplace: 'Mumbai', dlplace: 'Latur', service: 'Surface', mode: 'LTL', route: 'D2D',
  bkdate: '18-Jun-2026', bktime: '10:24', bkref: 'REF-8841', invno: 'INV-2261', invval: '24,500.00', ewb: '2810 0042 7781',
  pcs: '3', actwt: '48.0', chgwt: '52.0', cft: '6.4', dims: '40×30×25',
  consignor: 'Tata Motors Ltd', consignee: 'Ashok Hotel', cnaddr: 'Near District Court, Latur', cnmobile: '98220 11847', cnpin: '413512', area: 'Latur',
  freight: '1,840.00', other: '350.00', hamali: '120.00', fov: '60.00', docket: '50.00', gst: '415.80', amount: '2,470.00', total: '2,885.80', cod: '0.00',
  dlstatus: 'Delivered', statusdate: '20-Jun-2026', dldate: '20-Jun-2026', receiver: 'Ramesh', podstatus: 'Uploaded', attempts: '1', drsno: 'DRS-2606-00231', dboy: 'Ravi Kumar',
  remarks: '—', createdby: 'EMP-1042',
};
const PRESET = ['cnr', 'bkdate', 'bkplace', 'dlplace', 'pcs', 'chgwt', 'amount', 'other', 'total', 'dlstatus'];

let toastSeq = 0;

export default function ReportDefinition() {
  const rights = useRights(ReportDefinition_PATH);
  const [fields, setFields] = useState([]);             // master field list, from GET /get-report-fields
  const [defs, setDefs] = useState([]);                  // saved report definitions, from GET /get-report-definitions
  const [reportRows, setReportRows] = useState(null);    // real data rows for the loaded party/job, from GET /get-report-data
  const [parties, setParties] = useState([]);            // from GET /get-partylistAll → [{Party_id, Party_Name}]
  const [jobs, setJobs] = useState([]);                  // from GET /get-job-list/{partyId} → [{Job_Id, Job_Name}]
  const partyOpts = useMemo(() => parties.map((p) => ({ value: p.Party_id, label: p.Party_Name })), [parties]);
  const jobOpts = useMemo(() => jobs.map((j) => ({ value: j.Job_Id, label: j.Job_Name })), [jobs]);
  const [allJobsById, setAllJobsById] = useState({});    // from GET /get-all-jobs — used to label the saved-defs table
  const [curParty, setCurParty] = useState('');           // Party_id (number, as string while in a <select>)
  const [curJob, setCurJob] = useState('');               // Job_Id (number, as string while in a <select>)
  const [repName, setRepName] = useState('');
  const [selected, setSelected] = useState([]); // [{id,label,total}]
  const [availSearch, setAvailSearch] = useState('');
  const [toasts, setToasts] = useState([]);
  const dragIdx = useRef(null);

  const partyObj = parties.find((p) => String(p.Party_id) === String(curParty));
  const jobObj = jobs.find((j) => String(j.Job_Id) === String(curJob));

  /* ════════ JOB 2 — multi-select (checkbox dropdown) ════════
     Independent of the single "Job" select above. Uses the same `jobs`
     list (GET /get-job-list/{partyId}). Holds an array of Job_Id (strings). */
  const [multiJobs, setMultiJobs] = useState([]);
  const [multiOpen, setMultiOpen] = useState(false);
  const multiRef = useRef(null);

  // clear picks whenever the party changes (jobs list changes with it)
  useEffect(() => { setMultiJobs([]); setMultiOpen(false); }, [curParty]);

  // close dropdown on outside click
  useEffect(() => {
    function onDoc(e) {
      if (multiRef.current && !multiRef.current.contains(e.target)) setMultiOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  function toggleMultiJob(id) {
    const key = String(id);
    setMultiJobs((list) => (list.includes(key) ? list.filter((x) => x !== key) : [...list, key]));
  }
  const allMultiSelected = jobs.length > 0 && multiJobs.length === jobs.length;
  function toggleAllMultiJobs() {
    setMultiJobs(allMultiSelected ? [] : jobs.map((j) => String(j.Job_Id)));
  }
  const multiLabel =
    multiJobs.length === 0 ? '— Select Jobs —'
    : multiJobs.length === 1 ? (jobs.find((j) => String(j.Job_Id) === multiJobs[0])?.Job_Name || '1 job selected')
    : multiJobs.length + ' jobs selected';

  /* Auto-tick Job 2 checkboxes: when Job 1 already has a saved report, tick every
     OTHER job of this party that has the identical report (same name + same columns).
     Declared after the "clear on party change" effect so a party switch clears first. */
  useEffect(() => {
    if (!curParty || !curJob) return;
    const sig = (d) => JSON.stringify([d.name, (d.cols || []).map((c) => [c.id, c.label, !!c.total])]);
    const base = defs.find((x) => String(x.partyId) === String(curParty) && String(x.jobId) === String(curJob));
    if (!base) { setMultiJobs([]); return; }
    const baseSig = sig(base);
    const shared = defs
      .filter((x) => String(x.partyId) === String(curParty)
        && String(x.jobId) !== String(curJob)
        && sig(x) === baseSig
        && jobs.some((j) => String(j.Job_Id) === String(x.jobId)))
      .map((x) => String(x.jobId));
    setMultiJobs(shared);
  }, [curJob, curParty, defs, jobs]);

  const fieldById = (id) => fields.find((f) => f.id === id);
  const CATS = useMemo(() => {
    const seen = [];
    fields.forEach((f) => { if (!seen.includes(f.cat)) seen.push(f.cat); });
    return seen;
  }, [fields]);

  /* ════════ LOAD MASTER FIELDS FROM BACKEND ════════ */
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/get-report-fields');
        setFields(res.data || []);
      } catch (e) {
        console.error('Failed to load master fields', e);
        toast('Could not load field list from server', 'err');
      }
    })();
  }, []);

  /* ════════ LOAD PARTIES ════════ */
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/get-partylistAll');
        // Backend camelCases only the first letter (Party_id -> party_id,
        // Party_Name -> party_Name) — check both, same as ConsignmentBooking.jsx.
        // const normalized = (res.data || []).map((p) => ({
        //   Party_id: p.party_id ?? p.Party_id,
        //   Party_Name: p.party_Name ?? p.Party_Name,
        // }));
        const normalized = (res.data || []).map((p) => ({
  Party_id: p.party_Id ?? p.party_id ,
  // ?? p.Party_Id ?? p.Party_id,
  Party_Name: p.party_Name ?? p.Party_Name,
}));
        setParties(normalized);
      } catch (e) {
        console.error('Failed to load parties', e);
        toast('Could not load party list from server', 'err');
      }
    })();
  }, []);

  /* ════════ LOAD JOBS FOR THE SELECTED PARTY ════════ */
  useEffect(() => {
    if (!curParty) { setJobs([]); return; }
    (async () => {
      try {
        const res = await api.get(`/get-job-list/${curParty}`);
        const normalized = (res.data || []).map((j) => ({
          Job_Id: j.job_Id ?? j.Job_Id,
          Job_Name: j.job_Name ?? j.Job_Name,
        }));
        setJobs(normalized);
      } catch (e) {
        console.error('Failed to load jobs', e);
        toast('Could not load job list from server', 'err');
      }
    })();
  }, [curParty]);

  /* ════════ LOAD ALL JOBS (for labelling the saved-definitions table) ════════ */
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/get-all-jobs');
        const byId = {};
        (res.data || []).forEach((j) => {
          const jobId = j.job_Id ?? j.Job_Id;
          byId[jobId] = {
            Job_Id: jobId,
            Job_Name: j.job_Name ?? j.Job_Name,
            Party_Id: j.party_Id ?? j.Party_Id,
            Party_Name: j.party_Name ?? j.Party_Name,
          };
        });
        setAllJobsById(byId);
      } catch (e) {
        console.error('Failed to load all jobs', e);
      }
    })();
  }, []);

  /* ════════ LOAD SAVED REPORT DEFINITIONS FROM BACKEND ════════ */
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/get-report-definitions');
        setDefs(res.data || []);
      } catch (e) {
        console.error('Failed to load report definitions', e);
        toast('Could not load saved report definitions from server', 'err');
      }
    })();
  }, []);

  /* ════════ LOAD REAL REPORT DATA FOR CURRENT PARTY/JOB ════════ */
  useEffect(() => {
    if (!curParty || !curJob) { setReportRows(null); return; }
    (async () => {
      try {
        const res = await api.get('/get-report-data', { params: { partyId: curParty, jobId: curJob } });
        setReportRows(res.data.rows || []);
      } catch (e) {
        // 404 = no saved definition / no data yet — preview falls back to SAMPLE
        setReportRows(null);
      }
    })();
  }, [curParty, curJob]);

  /* ════════ TOAST ════════ */
  function toast(msg, type) {
    const id = ++toastSeq;
    setToasts((t) => [...t, { id, msg, type: type || 'info', leaving: false }]);
    setTimeout(() => {
      setToasts((t) => t.map((x) => (x.id === id ? { ...x, leaving: true } : x)));
      setTimeout(() => {
        setToasts((t) => t.filter((x) => x.id !== id));
      }, 320);
    }, 2700);
  }
  const TOAST_ICONS = { ok: '✓', err: '✕', warn: '⚠', info: 'ℹ' };

  /* ════════ PARTY / JOB ════════ */
  function partyChanged(value) {
    setCurParty(value);
    setCurJob('');
  }
  function jobChanged(value) {
    setCurJob(value);
    const d = defs.find((x) => String(x.partyId) === String(curParty) && String(x.jobId) === String(value));
    if (d) {
      setSelected(d.cols.map((c) => ({ ...c })));
      setRepName(d.name);
      toast('Loaded existing definition', 'info');
    } else {
      setSelected([]);
      setRepName('');
    }
  }

  /* ════════ AVAILABLE ════════ */
  const takenIds = useMemo(() => new Set(selected.map((s) => s.id)), [selected]);
  const availFields = useMemo(() => {
    const q = availSearch.toLowerCase().trim();
    return fields.filter((f) => !takenIds.has(f.id) && (!q || f.name.toLowerCase().includes(q) || f.cat.toLowerCase().includes(q)));
  }, [availSearch, takenIds, fields]);

  function addField(id) {
    if (selected.some((s) => s.id === id)) return;
    const f = fieldById(id);
    setSelected((s) => [...s, { id, label: f.name, total: false }]);
  }
  function addCat(cat) {
    setSelected((s) => {
      const existing = new Set(s.map((x) => x.id));
      const additions = fields.filter((f) => f.cat === cat && !existing.has(f.id)).map((f) => ({ id: f.id, label: f.name, total: false }));
      return [...s, ...additions];
    });
  }
  function addAll() {
    setSelected((s) => {
      const existing = new Set(s.map((x) => x.id));
      const additions = fields.filter((f) => !existing.has(f.id)).map((f) => ({ id: f.id, label: f.name, total: false }));
      return [...s, ...additions];
    });
    toast('All fields added', 'ok');
  }

  /* ════════ SELECTED ════════ */
  function removeField(i) {
    setSelected((s) => s.filter((_, idx) => idx !== i));
  }
  function clearAll() {
    if (!selected.length) return;
    if (!window.confirm('Remove all selected columns?')) return;
    setSelected([]);
  }
  function moveField(i, dir) {
    const j = i + dir;
    if (j < 0 || j >= selected.length) return;
    setSelected((s) => {
      const copy = [...s];
      [copy[i], copy[j]] = [copy[j], copy[i]];
      return copy;
    });
  }
  function setLabel(i, v) {
    setSelected((s) => s.map((item, idx) => (idx === i ? { ...item, label: v } : item)));
  }
  function setTotal(i, v) {
    setSelected((s) => s.map((item, idx) => (idx === i ? { ...item, total: v } : item)));
  }
  function loadPreset() {
    setSelected(PRESET.map((id) => {
      const f = fieldById(id);
      return { id, label: f.name, total: f.type === 'num' };
    }));
    toast('Standard consignment column set loaded', 'ok');
  }

  /* ════════ DRAG REORDER ════════ */
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [draggingIdx, setDraggingIdx] = useState(null);
  function dragStart(i) {
    dragIdx.current = i;
    setDraggingIdx(i);
  }
  function dragOver(e, i) {
    e.preventDefault();
    if (i !== dragIdx.current) setDragOverIdx(i);
  }
  function dragLeave() {
    setDragOverIdx(null);
  }
  function drop(e, i) {
    e.preventDefault();
    if (dragIdx.current === null || dragIdx.current === i) {
      dragEnd();
      return;
    }
    setSelected((s) => {
      const copy = [...s];
      const item = copy.splice(dragIdx.current, 1)[0];
      copy.splice(i, 0, item);
      return copy;
    });
    dragEnd();
  }
  function dragEnd() {
    dragIdx.current = null;
    setDraggingIdx(null);
    setDragOverIdx(null);
  }

  /* ════════ PREVIEW ════════ */
  const previewCols = selected.length + ' column' + (selected.length !== 1 ? 's' : '');
  const hasTotals = selected.some((s) => s.total);

  /* ════════ SAVE / LOAD ════════ */
  async function saveDefinition() {
    if (!curParty) { toast('Select a party', 'warn'); return; }
    // Targets = Job (single select) + every checked job in Job 2, de-duplicated.
    const targetJobIds = [...new Set([curJob, ...multiJobs].filter(Boolean).map(String))];
    if (!targetJobIds.length) { toast('Select a job', 'warn'); return; }
    if (!selected.length) { toast('Add at least one field', 'warn'); return; }
    const name = repName.trim() || 'Untitled Report';
    const cols = selected.map((s) => ({ ...s }));

    // Same definition is assigned to each target job (backend upserts per party+job).
    const results = await Promise.allSettled(
      targetJobIds.map((jid) =>
        api.post('/save-report-definition', {
          partyId: Number(curParty), jobId: Number(jid), reportName: name, columns: cols,
        })
      )
    );

    const savedRecs = [];
    let failed = 0;
    let firstErr = '';
    results.forEach((r) => {
      if (r.status === 'fulfilled') {
        const saved = r.value.data;
        savedRecs.push({ partyId: saved.partyId, jobId: saved.jobId, name: saved.name, cols: saved.cols, updated: new Date().toISOString().slice(0, 10) });
      } else {
        failed += 1;
        if (!firstErr) firstErr = r.reason?.response?.data?.message || r.reason?.message || 'Unknown error';
        console.error('saveDefinition error:', r.reason);
      }
    });

    if (savedRecs.length) {
      setDefs((d) => {
        let copy = [...d];
        savedRecs.forEach((rec) => {
          const idx = copy.findIndex((x) => String(x.partyId) === String(rec.partyId) && String(x.jobId) === String(rec.jobId));
          if (idx >= 0) copy[idx] = rec; else copy.push(rec);
        });
        return copy;
      });
    }

    if (!failed) {
      toast('Report definition saved for ' + savedRecs.length + ' job' + (savedRecs.length !== 1 ? 's' : '') + ' — ' + cols.length + ' columns', 'ok');
    } else if (savedRecs.length) {
      toast('Saved for ' + savedRecs.length + ' job(s), failed for ' + failed + ': ' + firstErr, 'warn');
    } else {
      toast('Could not save to server: ' + firstErr, 'err');
    }
  }
  function loadDef(partyId, jobId) {
    const d = defs.find((x) => String(x.partyId) === String(partyId) && String(x.jobId) === String(jobId));
    if (!d) return;
    setCurParty(String(partyId));
    setCurJob(String(jobId));
    setSelected(d.cols.map((c) => ({ ...c })));
    setRepName(d.name);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    toast('Loaded "' + d.name + '"', 'info');
  }
  async function delDef(partyId, jobId, ev) {
    ev.stopPropagation();
    const d = defs.find((x) => String(x.partyId) === String(partyId) && String(x.jobId) === String(jobId));
    if (!d) return;
    if (!window.confirm('Delete definition "' + d.name + '"?')) return;
    try {
      await api.post(`/delete-report-definition/${partyId}/${jobId}`);
      setDefs((list) => list.filter((x) => !(String(x.partyId) === String(partyId) && String(x.jobId) === String(jobId))));
      toast('Definition deleted', 'info');
    } catch (e) {
      console.error('delDef error:', e);
      toast('Could not delete on server', 'err');
    }
  }

  /* ════════ RESET ════════ */
  function resetBuilder() {
    if (selected.length && !window.confirm('Reset the current definition?')) return;
    setSelected([]);
    setRepName('');
  }

  const abForLabel = (partyObj && jobObj) ? ' · for ' + partyObj.Party_Name + ' / ' + jobObj.Job_Name : (partyObj ? ' · for ' + partyObj.Party_Name : '');

  return (
    <div className="red-root">
      {/* <header className="red-hdr">
        <div className="red-logo">
          <div className="red-logo-mark">
            <svg viewBox="0 0 24 24"><path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm13.5 0c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z" /></svg>
          </div>
          <div>
            <div className="red-logo-name">Saami <span>Tradestar</span></div>
            <div className="red-logo-pvt">Report Definition</div>
          </div>
        </div>
        <div className="red-hdr-user"><div className="red-hdr-av">AD</div>Admin</div>
      </header> */}

      {/* <div className="red-bc">
        <span>Reports</span><span className="red-bc-sep">›</span><span>Configuration</span><span className="red-bc-sep">›</span><span className="red-bc-cur">Report Definition</span>
      </div> */}

      <div className="red-page">
        <div className="red-pg-row">
          <div className="red-pg-title">
            <h1>Party / Job-wise Report Definition</h1>
            <p>Assign the fields each party &amp; job needs in their report — pick from the master list, order them, rename headers, and mark totals.</p>
          </div>
        </div>

        {/* SELECTION */}
        <div className="red-card">
          <div className="red-band red-navy-band">
            <div className="red-band-icon"><svg viewBox="0 0 24 24" strokeWidth="2"><path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /></svg></div>
            <div><div className="red-band-title">Report For</div><div className="red-band-sub">Choose the party and job this definition applies to</div></div>
          </div>
          <div className="red-card-body">
            <div className="red-g3">
              <div className="red-field">
                <label>Party <span className="red-req">*</span></label>
                <AutoComplete
                  options={partyOpts}
                  value={curParty}
                  onChange={(v) => partyChanged(v)}
                  placeholder="— Select Party —"
                />
              </div>
              <div className="red-field">
                <label>Job <span className="red-req">*</span> <span className="red-hint-inline">{curParty ? `(${jobs.length} job${jobs.length !== 1 ? 's' : ''})` : '(select party first)'}</span></label>
                <AutoComplete
                  options={jobOpts}
                  value={curJob}
                  onChange={(v) => jobChanged(v)}
                  placeholder={!curParty ? '(select party first)' : '— Select Job —'}
                  disabled={!curParty}
                />
              </div>
              <div className="red-field">
                <label>Report Name</label>
                <input type="text" value={repName} onChange={(e) => setRepName(e.target.value)} placeholder="e.g. Monthly Consignment MIS" />
              </div>
              <div className="red-field" ref={multiRef}>
                <label>Also Apply To<span className="red-hint-inline">{curParty ? `(${multiJobs.length} of ${jobs.length} selected)` : '(select first Job)'}</span></label>
                <div className="red-ms">
                  <button
                    type="button"
                    className={'red-ms-trigger' + (multiJobs.length ? ' red-has-val' : '')}
                    disabled={!curParty}
                    onClick={() => setMultiOpen((o) => !o)}
                  >
                    <span className="red-ms-text">{multiLabel}</span>
                  </button>
                  {multiOpen && (
                    <div className="red-ms-panel">
                      {jobs.length === 0 ? (
                        <div className="red-ms-empty">No jobs for this party</div>
                      ) : (
                        <>
                          <label className="red-ms-opt red-ms-all">
                            <input type="checkbox" checked={allMultiSelected} onChange={toggleAllMultiJobs} />
                            <span>Select all</span>
                          </label>
                          {jobs.map((j) => (
                            <label key={j.Job_Id} className="red-ms-opt">
                              <input
                                type="checkbox"
                                checked={multiJobs.includes(String(j.Job_Id))}
                                onChange={() => toggleMultiJob(j.Job_Id)}
                              />
                              <span>{j.Job_Name}</span>
                            </label>
                          ))}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* BUILDER */}
        <div className="red-card">
          <div className="red-band red-teal-band">
            <div className="red-band-icon"><svg viewBox="0 0 24 24" strokeWidth="2"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /></svg></div>
            <div><div className="red-band-title">Field Assignment</div><div className="red-band-sub">Add fields from the master list, then order &amp; configure them</div></div>
            <div className="red-band-r">
              <button className="red-btn red-btn-ghost red-btn-xs" onClick={loadPreset} style={{ background: 'rgba(255,255,255,.15)', color: '#fff', borderColor: 'rgba(255,255,255,.3)' }}>Load Standard Set</button>
            </div>
          </div>
          <div className="red-card-body">
            <div className="red-xfer">
              {/* AVAILABLE */}
              <div className="red-xfer-col">
                <div className="red-xfer-head">
                  <span className="red-xh-title">Available Fields</span><span className="red-xh-count">{availFields.length}</span>
                  <div className="red-xh-r"><button className="red-btn red-btn-ghost red-btn-xs" onClick={addAll}>Add All</button></div>
                </div>
                <div className="red-xfer-search">
                  <svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>
                  <input value={availSearch} onChange={(e) => setAvailSearch(e.target.value)} placeholder="Search fields…" />
                </div>
                <div className="red-xfer-body">
                  {!availFields.length ? (
                    <div className="red-xfer-empty"><div className="red-xe-ico">📋</div>{availSearch ? 'No matching fields' : 'All fields added'}</div>
                  ) : (
                    CATS.map((cat) => {
                      const items = availFields.filter((f) => f.cat === cat);
                      if (!items.length) return null;
                      return (
                        <div className="red-cat-group" key={cat}>
                          <div className="red-cat-h"><span>{cat}</span><span className="red-cat-add" onClick={() => addCat(cat)}>+ Add all</span></div>
                          {items.map((f) => (
                            <div className="red-avail-item" key={f.id} onClick={() => addField(f.id)}>
                              <span className="red-ai-plus">+</span>
                              <span className="red-ai-name">{f.name}</span>
                              <span className="red-ai-type">{f.type}</span>
                            </div>
                          ))}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* SELECTED */}
              <div className="red-xfer-col">
                <div className="red-xfer-head">
                  <span className="red-xh-title">Report Columns</span><span className="red-xh-count">{selected.length}</span>
                  <div className="red-xh-r"><button className="red-btn red-btn-ghost red-btn-xs" onClick={clearAll}>Clear</button></div>
                </div>
                <div className="red-xfer-search" style={{ color: 'var(--muted)', fontSize: '11px', justifyContent: 'center' }}>
                  Drag ⋮⋮ to reorder · edit header label · ✓ Σ for column totals
                </div>
                <div className="red-xfer-body">
                  {!selected.length ? (
                    <div className="red-xfer-empty"><div className="red-xe-ico">🗂️</div>Click fields on the left to add them as report columns</div>
                  ) : (
                    selected.map((s, i) => {
                      const f = fieldById(s.id) || { name: s.id, type: 'text' };
                      const isNum = f.type === 'num';
                      const itemClasses = ['red-sel-item'];
                      if (dragOverIdx === i) itemClasses.push('red-drag-over');
                      if (draggingIdx === i) itemClasses.push('red-dragging');
                      return (
                        <div
                          key={s.id}
                          className={itemClasses.join(' ')}
                          draggable="true"
                          onDragStart={() => dragStart(i)}
                          onDragOver={(e) => dragOver(e, i)}
                          onDragLeave={dragLeave}
                          onDrop={(e) => drop(e, i)}
                          onDragEnd={dragEnd}
                        >
                          <span className="red-sel-grip">⠿</span>
                          <span className="red-sel-seq">{i + 1}</span>
                          <div className="red-sel-main">
                            <div className="red-sel-name">{f.name} <span style={{ color: 'var(--muted)', fontSize: '9px' }}>· {f.type}</span></div>
                            <input className="red-sel-label" value={s.label} onChange={(e) => setLabel(i, e.target.value)} placeholder="Column header" />
                          </div>
                          <label className={'red-sel-total' + (isNum ? '' : ' red-dim')} title="Show column total">
                            <input type="checkbox" checked={s.total} disabled={!isNum} onChange={(e) => setTotal(i, e.target.checked)} /> Σ
                          </label>
                          <div className="red-sel-ctrls">
                            <button className="red-sc-btn" onClick={() => moveField(i, -1)} disabled={i === 0}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="18 15 12 9 6 15" /></svg></button>
                            <button className="red-sc-btn" onClick={() => moveField(i, 1)} disabled={i === selected.length - 1}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg></button>
                            <button className="red-sc-btn red-rm" onClick={() => removeField(i)}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg></button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* PREVIEW */}
        <div className="red-card">
          <div className="red-band red-blue-band">
            <div className="red-band-icon"><svg viewBox="0 0 24 24" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg></div>
            <div><div className="red-band-title">Report Preview</div><div className="red-band-sub">Columns in defined order with sample data</div></div>
            <div className="red-band-r"><span className="red-band-badge">{previewCols}</span></div>
          </div>
          <div className="red-card-body">
            {!selected.length ? (
              <div className="red-prev-empty"><div style={{ fontSize: '34px', opacity: .5 }}>👁️</div><div style={{ fontWeight: 700, color: 'var(--slate)', marginTop: '8px' }}>Add columns to preview the report</div></div>
            ) : (
              <div className="red-prev-scroll">
                {reportRows && <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '6px' }}>Showing live data from server ({reportRows.length} row{reportRows.length !== 1 ? 's' : ''})</div>}
                <table className="red-prev-tbl">
                  <thead>
                    <tr>
                      {selected.map((s) => {
                        const f = fieldById(s.id) || { type: 'text' };
                        return <th key={s.id} className={f.type === 'num' ? 'red-num' : ''}>{s.label || f.name}</th>;
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {(reportRows && reportRows.length ? reportRows.slice(0, 2) : [0, 1]).map((row, rowIdx) => (
                      <tr key={rowIdx}>
                        {selected.map((s) => {
                          const f = fieldById(s.id) || { type: 'text' };
                          const val = reportRows && reportRows.length
                            ? (row[s.id] !== undefined && row[s.id] !== null ? row[s.id] : '—')
                            : (SAMPLE[s.id] !== undefined ? SAMPLE[s.id] : '—');
                          return <td key={s.id} className={f.type === 'num' ? 'red-num' : ''}>{val}</td>;
                        })}
                      </tr>
                    ))}
                  </tbody>
                  {hasTotals && (
                    <tfoot>
                      <tr>
                        {selected.map((s, i) => {
                          const f = fieldById(s.id) || { type: 'text' };
                          if (i === 0) return <td key={s.id}>Total</td>;
                          return <td key={s.id} className={f.type === 'num' ? 'red-num' : ''}>{s.total ? (SAMPLE[s.id] || '') : ''}</td>;
                        })}
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            )}
          </div>
        </div>

        {/* SAVED */}
        <div className="red-card">
          <div className="red-band red-navy-band">
            <div className="red-band-icon"><svg viewBox="0 0 24 24" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /></svg></div>
            <div><div className="red-band-title">Saved Report Definitions</div><div className="red-band-sub">Click to load &amp; edit</div></div>
            <div className="red-band-r"><span className="red-band-badge">{defs.length}</span></div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="red-saved-tbl">
              <thead>
                <tr><th>Party</th><th>Job</th><th>Report Name</th><th className="red-c">Columns</th><th>Updated</th><th className="red-c">Action</th></tr>
              </thead>
              <tbody>
                {!defs.length ? (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: '30px', color: 'var(--muted)' }}>No saved definitions yet</td></tr>
                ) : (
                  defs.map((d) => {
                    const p = parties.find((x) => String(x.Party_id) === String(d.partyId));
                    const j = allJobsById[d.jobId];
                    const partyName = p ? p.Party_Name : `Party #${d.partyId}`;
                    const jobName = j ? j.Job_Name : `Job #${d.jobId}`;
                    return (
                      <tr key={d.partyId + '-' + d.jobId} onClick={() => loadDef(d.partyId, d.jobId)}>
                        <td style={{ fontWeight: 700, color: 'var(--navy)' }}>{partyName}</td>
                        <td style={{ fontSize: '12px' }}>{jobName}<div style={{ fontSize: '10px', color: 'var(--muted)', fontFamily: "'JetBrains Mono',monospace" }}>Job #{d.jobId}</div></td>
                        <td>{d.name}</td>
                        <td className="red-c"><span className="red-chip-count">{d.cols.length} cols</span></td>
                        <td style={{ color: 'var(--slate)', fontSize: '12px' }}>{d.updated}</td>
                        <td className="red-c">
                          {rights.delete && (
                          <button className="red-btn red-btn-ghost red-btn-xs" onClick={(e) => delDef(d.partyId, d.jobId, e)}>Delete</button>
                        )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ACTION BAR */}
      <div className="red-action-bar">
        <div className="red-ab-inner">
          <div className="red-ab-info"><b>{selected.length}</b> columns selected<span>{abForLabel}</span></div>
          <button className="red-btn red-btn-ghost" onClick={resetBuilder}>Reset</button>
          {rights.add &&(
            <button className="red-btn red-btn-green" onClick={saveDefinition}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v14a2 2 0 01-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" /></svg>
            Save Report Definition
          </button>
        )}
        </div>
      </div>

      <div className="red-toast-wrap">
        {toasts.map((t) => (
          <div key={t.id} className={'red-toast red-' + t.type + (t.leaving ? ' red-toast-out' : '')}>
            <span style={{ fontSize: '15px' }}>{TOAST_ICONS[t.type] || 'ℹ'}</span>{t.msg}
          </div>
        ))}
      </div>
    </div>
  );
}